import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, ButtonGroup, Button, ButtonProps } from '@chakra-ui/core';

const getButtonStyles = (
  isActive: boolean,
  colorScheme: 'red' | 'green',
): Pick<ButtonProps, 'borderColor' | 'color' | 'backgroundColor' | '_hover'> => {
  if (!isActive) {
    return {
      borderColor: 'gray.600',
      color: 'gray.600',
      backgroundColor: 'white',
      _hover: { backgroundColor: 'gray.50' },
    };
  }

  return {
    borderColor: `${colorScheme}.500`,
    color: `${colorScheme}.500`,
    backgroundColor: `${colorScheme}.50`,
    _hover: { backgroundColor: `${colorScheme}.100` },
  };
};

const ActionButton = React.memo<{
  isActive: boolean;
  colorScheme: 'red' | 'green';
  label: string;
  onClick: () => void;
  disabled: boolean;
  testId: string;
}>(({ isActive, colorScheme, label, onClick, disabled, testId }) => {
  const styles = useMemo(() => getButtonStyles(isActive, colorScheme), [isActive, colorScheme]);

  return (
    <Button {...styles} fontWeight="bold" onClick={onClick} disabled={disabled} data-testid={testId}>
      {label}
    </Button>
  );
});

ActionButton.displayName = 'ActionButton';

interface AcceptRejectActionsProps {
  questionId: string;
  hasBeenReviewed: boolean;
  isAnswerAccepted?: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

export const AcceptRejectActions = React.memo<AcceptRejectActionsProps>(function AcceptRejectActions({
  questionId,
  hasBeenReviewed,
  isAnswerAccepted,
  onChange,
  disabled = false,
}) {
  const { t } = useTranslation('common');

  const handleReject = useCallback(() => {
    onChange(false);
  }, [onChange]);

  const handleAccept = useCallback(() => {
    onChange(true);
  }, [onChange]);

  const isRejectActive = hasBeenReviewed && !isAnswerAccepted;
  const isAcceptActive = hasBeenReviewed && !!isAnswerAccepted;

  return (
    <Flex flexShrink={0} flexBasis="8rem" justifyContent="flex-end">
      <ButtonGroup size="xs" isAttached variant="outline">
        <ActionButton
          isActive={isRejectActive}
          colorScheme="red"
          label={t('toReviewQuestions.reject')}
          onClick={handleReject}
          disabled={disabled}
          testId={`rejectBtn-${questionId}`}
        />
        <ActionButton
          isActive={isAcceptActive}
          colorScheme="green"
          label={t('toReviewQuestions.accept')}
          onClick={handleAccept}
          disabled={disabled}
          testId={`acceptBtn-${questionId}`}
        />
      </ButtonGroup>
    </Flex>
  );
});
